import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { Avatar } from "primereact/avatar";
import { useNavigate } from "react-router-dom";
import { Employee } from "../../../../../models/DSF/OrganogramAndAppointments";
import { RouteComponentProps } from "../../../../../models/RouteComponent";
import { UserTypes } from "../../../../../enum/UserTypes";
import { s3GetUrl } from "../../../../../service/s3Service";
import "./NodeDetailsModal.scss";

interface NodeDetailsModalProps {
  visible: boolean;
  onHide: () => void;
  nodeData: {
    label: string;
    title?: string;
    accessLevel?: string;
    legalReference?: string;
    employees?: Employee[];
    isEmployee?: boolean;
  } | null;
  routeProps?: RouteComponentProps;
}

const NodeDetailsModal: React.FC<NodeDetailsModalProps> = ({
  visible,
  onHide,
  nodeData,
  routeProps,
}) => {
  const navigate = useNavigate();
  const [employeePhotos, setEmployeePhotos] = useState<{ [key: string]: string }>({});
  const [photosLoading, setPhotosLoading] = useState<{ [key: string]: boolean }>({});
  const [photosError, setPhotosError] = useState<{ [key: string]: boolean }>({});

  // Function to handle "View Profile" navigation
  const handleViewProfile = (employee: Employee) => {
    if (routeProps) {
      // Set the employee in global state
      if (routeProps.setEmployee) {
        routeProps.setEmployee(employee);
      }

      // Update breadcrumbs
      if (routeProps.setBreadcrumbs && routeProps.breadcrumbs) {
        const breadcrumbs = [...routeProps.breadcrumbs];
        breadcrumbs.push({
          label: `${employee.name} ${employee.surname}`,
          link: "/site-dsf-table/dsf-employee-database",
          userType: UserTypes.Site,
        });
        routeProps.setBreadcrumbs(breadcrumbs);
      }

      // Navigate to employee database
      navigate("/site-dsf-table/dsf-employee-database");
    } else {
      // Fallback if no routeProps are available
      console.log("Navigate to employee profile:", employee.id, employee.name, employee.surname);
    }
  };

  // Load employee photos when employees change
  useEffect(() => {
    if (!nodeData?.employees || nodeData.employees.length === 0) {
      setEmployeePhotos({});
      setPhotosLoading({});
      setPhotosError({});
      return;
    }

    const loadEmployeePhotos = async () => {
      const photoMap: { [key: string]: string } = {};
      const loadingMap: { [key: string]: boolean } = {};
      const errorMap: { [key: string]: boolean } = {};

      // Initialize loading states
      for (const employee of nodeData.employees!) {
        if (employee.s3_key) {
          loadingMap[employee.id] = true;
          errorMap[employee.id] = false;
        }
      }
      setPhotosLoading(loadingMap);
      setPhotosError(errorMap);

      for (const employee of nodeData.employees!) {
        if (employee.s3_key) {
          try {
            const photoUrl = await s3GetUrl(employee.s3_key);
            if (photoUrl) {
              photoMap[employee.id] = photoUrl;
            }
            errorMap[employee.id] = false;
          } catch (error) {
            console.error(`Failed to load photo for employee ${employee.id}:`, error);
            errorMap[employee.id] = true;
          } finally {
            loadingMap[employee.id] = false;
          }
        }
      }

      setEmployeePhotos(photoMap);
      setPhotosLoading({ ...loadingMap });
      setPhotosError({ ...errorMap });
    };

    loadEmployeePhotos();
  }, [nodeData?.employees]);

  if (!nodeData) return null;

  const { label, title, legalReference, employees = [], isEmployee } = nodeData;

  const headerTemplate = () => (
    <div className="node-details-header">
      <div className="header-content">
        <div className="node-icon">
          <i className={`pi ${isEmployee ? "pi-user" : "pi-sitemap"}`} />
        </div>
        <div className="header-text">
          <h3>{label}</h3>
          {(title || legalReference) && (
            <span className="header-subtitle">{title || legalReference}</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible}
      style={{ width: "600px", maxWidth: "90vw" }}
      header={headerTemplate}
      onHide={onHide}
      className="node-details-modal"
      modal
      draggable={false}
      resizable={false}
    >
      <div className="node-details-content">
        {/* Department/Role Information */}
        {!isEmployee && (
          <div className="section">
            <h4>Department Information</h4>
            <div className="info-grid">
              <div className="info-item">
                <label>Name:</label>
                <span>{label}</span>
              </div>
              {title && (
                <div className="info-item">
                  <label>Title:</label>
                  <span>{title}</span>
                </div>
              )}
              {legalReference && (
                <div className="info-item">
                  <label>Legal Reference:</label>
                  <Chip label={legalReference} className="legal-reference-chip" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Employees Section */}
        {employees.length > 0 ? (
          <div className="section">
            <h4>Team Members ({employees.length})</h4>
            <div className="employees-grid">
              {employees.map((employee, index) => (
                <div key={employee.id || index} className="employee-card">
                  <div className="employee-header">
                    <div className="employee-avatar">
                      {photosLoading[employee.id] ? (
                        <div
                          style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "50%",
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #dee2e6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <i
                            className="pi pi-spinner pi-spin"
                            style={{ fontSize: "1.5rem", color: "#6c757d" }}
                          />
                        </div>
                      ) : employeePhotos[employee.id] && !photosError[employee.id] ? (
                        <img
                          src={employeePhotos[employee.id]}
                          alt={`${employee.name} ${employee.surname}`}
                          style={{
                            width: "60px",
                            height: "60px",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                          onError={e => {
                            // Set error state for this employee
                            setPhotosError(prev => ({ ...prev, [employee.id]: true }));
                            // Fallback to thumbnail
                            e.currentTarget.src =
                              "https://ionicframework.com/docs/img/demos/thumbnail.svg";
                          }}
                          onLoad={() => {
                            // Ensure error state is cleared on successful load
                            setPhotosError(prev => ({ ...prev, [employee.id]: false }));
                          }}
                        />
                      ) : (
                        <Avatar
                          label={`${employee.name?.charAt(0) || ""}${employee.surname?.charAt(0) || ""}`}
                          size="large"
                          shape="circle"
                          style={{ backgroundColor: "#3b82f6", color: "white" }}
                        />
                      )}
                    </div>
                    <div className="employee-info">
                      <div className="employee-name">
                        {employee.name} {employee.surname}
                      </div>
                      {employee.title && <div className="employee-title">{employee.title}</div>}
                      {employee.email && (
                        <div className="employee-contact">
                          <i className="pi pi-envelope" />
                          <a href={`mailto:${employee.email}`}>{employee.email}</a>
                        </div>
                      )}
                      {employee.mobile && (
                        <div className="employee-contact">
                          <i className="pi pi-phone" />
                          <a href={`tel:${employee.mobile}`}>{employee.mobile}</a>
                        </div>
                      )}
                      <div className="employee-status">
                        <Chip
                          label={employee.active ? "Active" : "Inactive"}
                          className={employee.active ? "status-active" : "status-inactive"}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="employee-actions">
                    <Button
                      label="View Profile"
                      icon="pi pi-user"
                      onClick={() => handleViewProfile(employee)}
                      className="p-button-outlined p-button-sm"
                      size="small"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          !isEmployee && (
            <div className="section">
              <h4>Team Members (0)</h4>
              <div className="empty-state">
                <div className="empty-state-icon">
                  <i className="pi pi-users" />
                </div>
                <div className="empty-state-text">
                  <p>No employees assigned to this department yet.</p>
                  <small>Employees can be assigned through the employee management system.</small>
                </div>
              </div>
            </div>
          )
        )}

        {/* Statistics */}
        {!isEmployee && (
          <div className="section">
            <h4>Department Statistics</h4>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="pi pi-users" />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{employees.length}</div>
                  <div className="stat-label">Total Employees</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon active">
                  <i className="pi pi-check-circle" />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{employees.filter(emp => emp.active).length}</div>
                  <div className="stat-label">Active Employees</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon inactive">
                  <i className="pi pi-times-circle" />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{employees.filter(emp => !emp.active).length}</div>
                  <div className="stat-label">Inactive Employees</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon roles">
                  <i className="pi pi-briefcase" />
                </div>
                <div className="stat-content">
                  <div className="stat-value">
                    {new Set(employees.map(emp => emp.title).filter(Boolean)).size}
                  </div>
                  <div className="stat-label">Unique Roles</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon contact">
                  <i className="pi pi-phone" />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{employees.filter(emp => emp.mobile).length}</div>
                  <div className="stat-label">With Contact Info</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon email">
                  <i className="pi pi-envelope" />
                </div>
                <div className="stat-content">
                  <div className="stat-value">{employees.filter(emp => emp.email).length}</div>
                  <div className="stat-label">With Email</div>
                </div>
              </div>
            </div>

            {/* Department Summary */}
            <div className="department-summary">
              <div className="summary-item">
                <span className="summary-label">Department Capacity:</span>
                <span className="summary-value">
                  {employees.filter(emp => emp.active).length} / {employees.length}
                  <span className="percentage">
                    (
                    {employees.length > 0
                      ? Math.round(
                          (employees.filter(emp => emp.active).length / employees.length) * 100
                        )
                      : 0}
                    %)
                  </span>
                </span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Most Common Role:</span>
                <span className="summary-value">
                  {(() => {
                    const roles = employees.map(emp => emp.title).filter(Boolean);
                    if (roles.length === 0) return "No roles defined";
                    const roleCount = roles.reduce(
                      (acc, role) => {
                        acc[role] = (acc[role] || 0) + 1;
                        return acc;
                      },
                      {} as Record<string, number>
                    );
                    const mostCommon = Object.entries(roleCount).sort(([, a], [, b]) => b - a)[0];
                    return mostCommon ? `${mostCommon[0]} (${mostCommon[1]})` : "No roles defined";
                  })()}
                </span>
              </div>
              {legalReference && (
                <div className="summary-item">
                  <span className="summary-label">Legal Compliance:</span>
                  <Chip label={legalReference} className="legal-reference-chip" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default NodeDetailsModal;
